import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { ApplicationApi } from "@/api";
import {
  enums as returnStatusEnums,
  OPEN_STATUS_FILTER,
} from "@/modules/applications/enums/return-status.enum";

const defaultFilters = () => ({
  // По умолчанию показываем открытые возвраты (initiated + partially_allocated)
  status: OPEN_STATUS_FILTER,
  transport_id: null,
  date_from: null,
  date_to: null,
});

const defaultPagination = () => ({
  page: 1,
  lastPage: 1,
  total: 0,
  perPage: 10,
});

/**
 * Стор страницы «Возвраты остатков» и блока возвратов на странице заявки.
 */
export const useReturnsStore = defineStore("applicationReturns", () => {
  // ----- state -----
  const returns = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const filters = ref(defaultFilters());
  const pagination = ref(defaultPagination());
  const currentReturn = ref(null);
  const loadingCurrent = ref(false);
  // Загрузка операции распределения / отклонения
  const loadingDecision = ref(false);
  // Количество открытых возвратов (бейдж в меню)
  const openCount = ref(0);
  const loadingOpenCount = ref(false);

  // ----- getters -----
  const getReturns = computed(() => returns.value);
  const isLoading = computed(() => loading.value);
  const hasOpenReturns = computed(() => openCount.value > 0);

  // ----- actions -----
  /**
   * Список возвратов с фильтрами и пагинацией
   * @param {Object} params - Дополнительные параметры (page и т.д.)
   * @returns {Promise<Array>}
   */
  async function fetchReturns(params = {}) {
    loading.value = true;
    error.value = null;

    try {
      const query = {
        ...filters.value,
        page: pagination.value.page,
        per_page: pagination.value.perPage,
        ...params,
      };

      Object.keys(query).forEach((key) => {
        if (query[key] === null || query[key] === undefined || query[key] === "") {
          delete query[key];
        }
      });

      const { data, meta } = await ApplicationApi.getReturns(query);

      returns.value = data || [];

      if (meta) {
        pagination.value = {
          page: meta.current_page || 1,
          lastPage: meta.last_page || 1,
          total: meta.total || 0,
          perPage: meta.per_page || pagination.value.perPage,
        };
      }

      return returns.value;
    } catch (e) {
      console.error("Ошибка при получении списка возвратов:", e);
      setError(e);
      returns.value = [];
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Карточка возврата
   * @param {Number|String} returnId
   * @returns {Promise<Object|null>}
   */
  async function fetchReturnById(returnId) {
    loadingCurrent.value = true;

    try {
      const { data } = await ApplicationApi.getReturn(returnId);
      currentReturn.value = data || null;
      return currentReturn.value;
    } catch (e) {
      console.error(`Ошибка при получении возврата с ID ${returnId}:`, e);
      setError(e);
      return null;
    } finally {
      loadingCurrent.value = false;
    }
  }

  /**
   * Количество открытых возвратов (initiated + partially_allocated) для бейджа меню.
   * Бэк отдаёт meta.total, поэтому per_page=1 достаточно.
   * @returns {Promise<Number>}
   */
  async function fetchOpenCount() {
    if (loadingOpenCount.value) return openCount.value;
    loadingOpenCount.value = true;

    try {
      const [initiated, partial] = await Promise.all([
        ApplicationApi.getReturns({ status: returnStatusEnums.INITIATED, per_page: 1 }),
        ApplicationApi.getReturns({ status: returnStatusEnums.PARTIALLY_ALLOCATED, per_page: 1 }),
      ]);

      openCount.value = (initiated?.meta?.total || 0) + (partial?.meta?.total || 0);
      return openCount.value;
    } catch (e) {
      console.error("Ошибка при получении количества открытых возвратов:", e);
      return openCount.value;
    } finally {
      loadingOpenCount.value = false;
    }
  }

  /**
   * Распределение остатка (на базу / на другую заявку)
   * @param {Object} payload - см. ApplicationApi.allocateReturn
   * @returns {Promise<Object>} { returns, tank?, operation?, destination_application? }
   */
  async function allocateReturn(payload) {
    loadingDecision.value = true;

    try {
      const response = await ApplicationApi.allocateReturn(payload);
      const updated = response?.returns || response?.data?.returns || [];

      // Обновляем возвраты в списке по id
      updated.forEach((item) => {
        const index = returns.value.findIndex((r) => r.id === item.id);
        if (index !== -1) {
          returns.value[index] = item;
        }
        if (currentReturn.value && currentReturn.value.id === item.id) {
          currentReturn.value = item;
        }
      });

      return response;
    } catch (e) {
      console.error("Ошибка при распределении возврата:", e);
      throw e;
    } finally {
      loadingDecision.value = false;
    }
  }

  /**
   * Отклонение возврата
   * @param {Number|String} returnId
   * @param {String} reason
   * @returns {Promise<Object>}
   */
  async function rejectReturn(returnId, reason) {
    loadingDecision.value = true;

    try {
      const response = await ApplicationApi.rejectReturn(returnId, { reason });
      const data = response?.data;

      if (data) {
        const index = returns.value.findIndex((r) => r.id === data.id);
        if (index !== -1) {
          returns.value[index] = data;
        }
        if (currentReturn.value && currentReturn.value.id === data.id) {
          currentReturn.value = data;
        }
      }

      return response;
    } catch (e) {
      console.error(`Ошибка при отклонении возврата с ID ${returnId}:`, e);
      throw e;
    } finally {
      loadingDecision.value = false;
    }
  }

  function setPage(page) {
    pagination.value.page = page || 1;
  }

  function resetFilters() {
    filters.value = defaultFilters();
    pagination.value = defaultPagination();
  }

  function resetStore() {
    returns.value = [];
    loading.value = false;
    error.value = null;
    filters.value = defaultFilters();
    pagination.value = defaultPagination();
    currentReturn.value = null;
    loadingCurrent.value = false;
    loadingDecision.value = false;
  }

  function setError(value) {
    error.value = value;
  }

  return {
    // state
    returns,
    loading,
    error,
    filters,
    pagination,
    currentReturn,
    loadingCurrent,
    loadingDecision,
    openCount,
    loadingOpenCount,
    // getters
    getReturns,
    isLoading,
    hasOpenReturns,
    // actions
    fetchReturns,
    fetchReturnById,
    fetchOpenCount,
    allocateReturn,
    rejectReturn,
    setPage,
    resetFilters,
    resetStore,
  };
});
